import { Message, WidgetState } from '@/types/widget';
import { WidgetStoreSliceCreator } from '../types';
import { CustomContext } from '@/types/context';

export interface ChatStore {
  chat: {
    // State
    sessionId: string | null;
    sessionToken: string | null;
    conversationId: string | null;
    messages: Message[];
    isTyping: boolean;
    connectionStatus: WidgetState['connectionStatus'];
    filledQuestionnaireData: Record<string, string>;
    lastWelcomeBackShown: number | null;
    welcomeBackMessageIndex: number;
    isFormSubmitting: boolean;
    lockedWelcomeMessage: string | null;
    lockedCustomContext: CustomContext | null;
    unreadCount: number;
    // Actions
    setSessionId: (sessionId: string | null) => void;
    setSessionToken: (token: string | null) => void;
    setConversationId: (conversationId: string | null) => void;
    setConnectionStatus: (
      connectionStatus: WidgetState['connectionStatus']
    ) => void;
    setIsTyping: (isTyping: boolean) => void;
    addMessages: (messages: Message[]) => void;
    clearMessages: () => void;
    clearSystemMessages: () => void;
    setFilledQuestionnaireData: (
      filledQuestionnaireData: Record<string, string>
    ) => void;
    setLastWelcomeBackShown: (timestamp: number | null) => void;
    setWelcomeBackMessageIndex: (index: number) => void;
    setIsFormSubmitting: (isFormSubmitting: boolean) => void;
    lockWelcomeMessageAndContext: (
      welcomeMessage: string,
      customContext: CustomContext
    ) => void;
    incrementUnreadCount: () => void;
    resetUnreadCount: () => void;
    clearSession: () => void;
  };
}

export const createChatSlice: WidgetStoreSliceCreator<ChatStore> = set => ({
  chat: {
    // State
    sessionId: null,
    sessionToken: null,
    conversationId: null,
    isTyping: false,
    messages: [],
    connectionStatus: 'disconnected',
    filledQuestionnaireData: {},
    lastWelcomeBackShown: null,
    welcomeBackMessageIndex: 0,
    isFormSubmitting: false,
    lockedWelcomeMessage: null,
    lockedCustomContext: null,
    unreadCount: 0,
    // Actions
    setSessionId: (sessionId: string | null) =>
      set(state => ({ chat: { ...state.chat, sessionId } })),
    setSessionToken: (sessionToken: string | null) =>
      set(state => ({ chat: { ...state.chat, sessionToken } })),
    setConversationId: (conversationId: string | null) =>
      set(state => ({ chat: { ...state.chat, conversationId } })),
    setConnectionStatus: (connectionStatus: WidgetState['connectionStatus']) =>
      set(state => ({ chat: { ...state.chat, connectionStatus } })),
    setIsTyping: (isTyping: boolean) =>
      set(state => ({ chat: { ...state.chat, isTyping } })),
    addMessages: (messages: Message[]) => {
      return set(state => {
        // Combine existing and new messages
        const allMessages = [...state.chat.messages, ...messages];

        // Deduplicate by message ID
        const messageMap = new Map();
        allMessages.forEach(message => {
          messageMap.set(message.id, message);
        });

        // Sort by timestamp (chronological order)
        const sortedMessages = Array.from(messageMap.values()).sort(
          (a, b) => a.timestamp - b.timestamp
        );

        return {
          chat: {
            ...state.chat,
            messages: sortedMessages,
          },
        };
      });
    },
    clearMessages: () => {
      return set(state => {
        return {
          chat: {
            ...state.chat,
            messages: [],
          },
        };
      });
    },
    clearSystemMessages: () =>
      set(state => ({
        chat: {
          ...state.chat,
          messages: state.chat.messages.filter(
            message => message.sender !== 'system'
          ),
        },
      })),
    setFilledQuestionnaireData: (
      filledQuestionnaireData: Record<string, string>
    ) => {
      return set(state => ({
        chat: { ...state.chat, filledQuestionnaireData },
      }));
    },
    setLastWelcomeBackShown: (timestamp: number | null) =>
      set(state => ({
        chat: { ...state.chat, lastWelcomeBackShown: timestamp },
      })),
    setWelcomeBackMessageIndex: (index: number) =>
      set(state => ({
        chat: { ...state.chat, welcomeBackMessageIndex: index },
      })),
    setIsFormSubmitting: (isFormSubmitting: boolean) =>
      set(state => ({ chat: { ...state.chat, isFormSubmitting } })),
    lockWelcomeMessageAndContext: (
      welcomeMessage: string,
      customContext: CustomContext
    ) => {
      return set(state => ({
        chat: {
          ...state.chat,
          lockedWelcomeMessage: welcomeMessage,
          lockedCustomContext: customContext,
        },
      }));
    },
    incrementUnreadCount: () =>
      set(state => ({
        chat: { ...state.chat, unreadCount: state.chat.unreadCount + 1 },
      })),
    resetUnreadCount: () =>
      set(state => ({
        chat: { ...state.chat, unreadCount: 0 },
      })),
    clearSession: () => {
      // Clear stored proposalId from localStorage
      localStorage.removeItem('peptalk_last_proposal_id');

      return set(state => ({
        chat: {
          ...state.chat,
          sessionId: null,
          sessionToken: null,
          conversationId: null,
          messages: [],
          filledQuestionnaireData: {},
          lastWelcomeBackShown: null,
          welcomeBackMessageIndex: 0,
          isFormSubmitting: false,
          isTyping: false,
          connectionStatus: 'disconnected',
          lockedWelcomeMessage: null,
          lockedCustomContext: null,
          unreadCount: 0,
        },
      }));
    },
  },
});
